import { SessionChangedError } from '../session-scope'

interface OwnerSnapshot {
  assertCurrent(): void
}

/** Capture invocation ownership before entering a shared FIFO, not after it. */
export function createOwnedGate(
  gate: <T>(run: () => Promise<T>) => Promise<T>,
  capture: () => OwnerSnapshot,
) {
  return <T>(run: () => Promise<T>): Promise<T> => {
    const owner = capture()
    return gate(async () => {
      owner.assertCurrent()
      return run()
    })
  }
}

export function createOwnedResume(options: {
  capture(): OwnerSnapshot
  ownerId(): string | null
  restore(): Promise<void>
  resumePaused(): Promise<unknown>
  queued(ownerId: string): Array<() => Promise<void>>
  prune(): void
  reportFailure(): void
}) {
  let tail: Promise<void> = Promise.resolve()
  return (): Promise<void> => {
    const ownerId = options.ownerId()
    if (!ownerId) return Promise.resolve()
    let session: OwnerSnapshot
    try {
      session = options.capture()
    } catch (error) {
      if (!(error instanceof SessionChangedError)) options.reportFailure()
      return Promise.resolve()
    }
    const assertCurrent = () => {
      session.assertCurrent()
      if (!ownerId || options.ownerId() !== ownerId)
        throw new SessionChangedError()
    }
    tail = tail
      .then(async () => {
        assertCurrent()
        await options.restore()
        assertCurrent()
        await options.resumePaused()
        assertCurrent()
        for (const run of options.queued(ownerId)) {
          assertCurrent()
          await run()
        }
        assertCurrent()
        options.prune()
      })
      .catch(error => {
        if (!(error instanceof SessionChangedError)) options.reportFailure()
      })
    return tail
  }
}

/** Deduplicate storage reads per captured login; completion never changes owners. */
export function createOwnedRestore(options: {
  capture(): OwnerSnapshot & { identity: string | null; generation: number }
  ownerId(): string | null
  restore(ownerId: string | null): Promise<void>
}) {
  const restores = new Map<string, Promise<void>>()
  return async () => {
    const session = options.capture()
    const ownerId = options.ownerId()
    const key = JSON.stringify([session.identity, session.generation, ownerId])
    let pending = restores.get(key)
    if (!pending) {
      pending = options.restore(ownerId)
      restores.set(key, pending)
    }
    try {
      await pending
      session.assertCurrent()
      if (options.ownerId() !== ownerId) throw new SessionChangedError()
    } finally {
      if (restores.get(key) === pending) restores.delete(key)
    }
  }
}
